import React from 'react';
import {
  SimpleShowLayout,
  TextField,
  BooleanField,
  FunctionField,
  useRecordContext,
  NumberField,
} from 'react-admin';
import DateTimeField from 'components/DateTimeField';
import MetabaseUrlField from 'components/MetabaseUrlField';
import useCheckAccess from 'hooks/useCheckAccess';
import ReferenceField from 'components/ReferenceField';
import { DisplayHelpers } from 'utils/helpers';
import { REFERRAL_SOURCE_ID } from 'resources/referral_sources/util';
import MoneyField from 'components/MoneyField';
import { JobReleaseCategoriesShowField } from '../JobReleaseCategoriesInput';

const InfoTab = () => {
  const checkAccess = useCheckAccess();
  const record = useRecordContext();
  const isReferralSourceOther = record.referral_source_id === REFERRAL_SOURCE_ID.Other;

  return (
    <SimpleShowLayout>
      {checkAccess('view', 'Company', 'id') && <TextField label="ID" source="id" />}
      {checkAccess('view', 'Company', 'name') && <TextField label="Name" source="name" />}
      {checkAccess('view', 'Company', 'phone_number') && (
        <TextField label="Phone Number" source="phone_number" />
      )}
      {checkAccess('view', 'Company', 'vms_type_id') && (
        <TextField label="VMS Type" source="vms_type.label" />
      )}
      {checkAccess('view', 'Company', 'health_system_id') && (
        <TextField label="Health System" source="health_system.label" />
      )}
      <FunctionField
        label="Accepted Facility TOS"
        render={(record) => (
          <BooleanField record={{ ...record, accepted_tos: true }} source="accepted_tos" />
        )}
      />
      {['sales_associate_id', 'account_manager_id'].map((source) => (
        <ReferenceField
          key={source}
          reference="Administrator"
          source={source}
          gql={[
            ` account {
          id
          first_name
          last_name
        }`,
          ]}
        >
          <FunctionField render={DisplayHelpers.professional.fullName} />
        </ReferenceField>
      ))}
      {checkAccess('view', 'Company', 'msp_id') && <TextField label="MSP" source="msp.label" />}
      {checkAccess('view', 'Company', 'locations') && (
        <TextField label="Onboarding Status" source="onboarding_status" />
      )}
      {checkAccess('view', 'Company', 'verified') && (
        <BooleanField label="Verified" source="verified" />
      )}
      {checkAccess('view', 'Company', 'enable_template_rate') && (
        <BooleanField label="Enable Template Rate" source="enable_template_rate" />
      )}
      <BooleanField label="SaaS Credentialing Enabled" source="saas_credentialing_enabled" />
      <BooleanField label="SaaS Scheduling Enabled" source="saas_scheduling_enabled" />
      <BooleanField label="Display Pro EHR Information" source="display_pro_ehr_information" />
      {checkAccess('view', 'Company', 'include_pro_private_info_on_exports') && (
        <BooleanField
          label="Include Pro Private Info on Exports"
          source="include_pro_private_info_on_exports"
        />
      )}
      {checkAccess('view', 'Company', 'dispute_submission_window_days') && (
        <NumberField
          label="Dispute Submission Window Days"
          source="dispute_submission_window_days"
        />
      )}
      {checkAccess('view', 'Company', 'is_self_service') && (
        <BooleanField label="Is Self Serve" source="is_self_service" />
      )}
      {checkAccess('view', 'Company', 'hide_invoicing_during_onboarding') && (
        <BooleanField
          label="Hide Invoicing During Onboarding"
          source="hide_invoicing_during_onboarding"
        />
      )}
      {checkAccess('view', 'Company', 'uid') && <TextField label="UID" source="uid" />}
      {checkAccess('view', 'Company', 'onboarding_complete') && (
        <BooleanField label="Onboarding Complete" source="onboarding_complete" />
      )}
      {checkAccess('view', 'Company', 'agency_enabled') && (
        <BooleanField label="Agency Enabled" source="agency_enabled" />
      )}
      {checkAccess('view', 'Company', 'rfm') && <TextField label="RFM" source="rfm" />}
      {checkAccess('view', 'Company', 'referral_source') && (
        <TextField label="Referral Source" source="referral_source.label" />
      )}
      {checkAccess('view', 'Company', 'sales_tier') && (
        <TextField label="Sales Tier" source="sales_tier" />
      )}
      {checkAccess('view', 'Company', 'other_referral_source') && isReferralSourceOther && (
        <TextField label="Other Referral Source" source="other_referral_source" />
      )}
      {checkAccess('view', 'Company', 'disabled_at') && (
        <DateTimeField label="Disabled At" source="disabled_at" format="MM/dd/yyyy HH:mm" />
      )}
      {checkAccess('view', 'Company', 'last_time_job_posted_at') && (
        <DateTimeField
          label="Last Time Job Posted"
          source="last_time_job_posted_at"
          format="MM/dd/yyyy HH:mm"
        />
      )}
      {checkAccess('view', 'Company', 'detailed_notes') && (
        <TextField label="Detailed Notes" source="detailed_notes" />
      )}
      {checkAccess('view', 'Audit') && <MetabaseUrlField />}
      {checkAccess('view', 'Company', 'disable_rate_editing') && (
        <BooleanField label="Disable Rate Editing" source="disable_rate_editing" />
      )}
      {checkAccess('view', 'Company', 'lx_ns_credit_window_hours') && (
        <NumberField label="LX/NS Credit Window Hours" source="lx_ns_credit_window_hours" />
      )}
      {checkAccess('view', 'Company', 'lx_ns_credit_amount_cents') && (
        <MoneyField label="LX/NS Credit Dollar Amount" source="lx_ns_credit_amount_cents" />
      )}
      {checkAccess('view', 'Company', 'lx_ns_credit_starts_at') && (
        <DateTimeField
          label="LX/NS Credit Starts At"
          source="lx_ns_credit_starts_at"
          format="MM/dd/yyyy HH:mm"
        />
      )}
      <JobReleaseCategoriesShowField />
    </SimpleShowLayout>
  );
};

export default InfoTab;
